import { Transform } from '../../engine/core/Transform';
import { Vec3 } from '../../engine/math/vec3';
import { AABB } from '../../engine/math/aabb';
import { Rng } from '../../engine/math/random';
import { clamp } from '../../engine/math/easing';
import { plane, cylinder, box, combine } from '../../engine/geo/primitives';
import { translate, setEmissive, colorize } from '../../engine/geo/mesh';
import { GpuMesh, INSTANCE_STRIDE } from '../../engine/render/GpuMesh';
import type { Ctx } from '../Ctx';
import { PALETTE, BIOMES, SPOTS } from '../world/Layout';
import { worldHeight, MARSH_ISLANDS, MARSH_WATER_Y } from '../world/Terrain';
import { instanced, jitterInstance, treeMesh, reedMesh, Instance } from '../world/Props';

export interface MarshHandles {
  /** Per-step: brighten reed tips near the player (§2.2). */
  update(playerPos: Vec3, dt: number): void;
}

/** Sallow Marsh (§2.2): water plane colored by depth, bioluminescent reeds, sunken shrine, dead trees. */
export function buildMarsh(ctx: Ctx): MarshHandles {
  const P = PALETTE.marsh;
  const rng = new Rng(202);
  const st = ctx.statics;
  const b = BIOMES.marsh;
  const h = worldHeight;

  // water surface: one subdivided plane colored by terrain depth (teal shallow → black deep)
  const w = plane(200, 200, 100, 100, [0.2, 0.42, 0.4]);
  translate(w, b.center.x, MARSH_WATER_Y, b.center.z);
  colorize(w, (p) => {
    const d = MARSH_WATER_Y - h(p.x, p.z);
    if (d < 0) return [0.2, 0.42, 0.4];
    const k = clamp((d - 0.6) / 1.2, 0, 1);
    return [0.2 * (1 - k) + 0.01 * k, 0.42 * (1 - k) + 0.01 * k, 0.4 * (1 - k) + 0.02 * k];
  });
  setEmissive(w, [0.1, 0.25, 0.22], 0.25);
  ctx.spawn('marsh.water', new Transform(), w, { transparent: true, alpha: 0.7, castShadow: false, wind: 0.3, noCull: true });

  // dead trees on islands and the shore
  const trees: Instance[] = [];
  for (let i = 0; i < 40; i++) {
    const isl = rng.pick(MARSH_ISLANDS);
    const a = rng.range(0, 6.3), r = rng.range(2, isl.r - 1);
    const x = isl.x + Math.cos(a) * r, z = isl.z + Math.sin(a) * r;
    if (Math.hypot(x - SPOTS.marshShrine.x, z - SPOTS.marshShrine.z) < 8) continue;
    if (Math.hypot(x - SPOTS.marshNearIsland.x, z - SPOTS.marshNearIsland.z) < 3) continue;
    if (Math.abs(z) < 6 && x > -160) continue;
    trees.push(jitterInstance(rng, new Vec3(x, h(x, z) - 0.3, z), 0.15));
    st.addCylinder(x, z, 0.4, h(x, z) - 1, h(x, z) + 6, 'trunk');
  }
  instanced(ctx, 'marsh.trees', treeMesh(P[2], [0.2, 0.3, 0.22], 6, 0.35, 1.4, 5), trees, { wind: 0.2 });

  // reeds: in shallow water and at island edges, instanced; tips brighten near the player
  const reeds: Instance[] = [];
  for (let i = 0; i < 700; i++) {
    const x = b.center.x + rng.range(-b.radius, b.radius), z = b.center.z + rng.range(-b.radius, b.radius);
    if (Math.hypot(x - b.center.x, z - b.center.z) > b.radius - 3) continue;
    const y = h(x, z);
    if (y < -1.0 || y > 0.7) continue;
    if (Math.abs(z) < 5 && x > -165) continue;
    reeds.push({ pos: new Vec3(x, y, z), yaw: rng.range(0, 6.3), scale: rng.range(0.7, 1.3), tilt: rng.range(-0.1, 0.1), emissive: 0 });
  }
  const reedInst = instanced(ctx, 'marsh.reeds', reedMesh([0.3, 0.42, 0.28], P[4]), reeds, { wind: 1.1, castShadow: false });

  // sunken shrine on the far island: ring of broken pillars + pedestal for the Gold Key
  const s = SPOTS.marshShrine;
  const sy = h(s.x, s.z);
  const shrine = [translate(cylinder(2.2, 2.6, 0.5, 10, P[3]), s.x, sy + 0.25, s.z), translate(box(0.8, 0.9, 0.8, P[3]), s.x, sy + 0.95, s.z)];
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2, px = s.x + Math.cos(a) * 4.5, pz = s.z + Math.sin(a) * 4.5;
    const ph = rng.range(1.5, 4.5);
    shrine.push(translate(cylinder(0.45, 0.5, ph, 7, P[3]), px, h(px, pz) + ph / 2 - 0.2, pz));
    st.addCylinder(px, pz, 0.5, h(px, pz) - 1, h(px, pz) + ph, 'pillar');
  }
  st.addCylinder(s.x, s.z, 0.6, sy - 1, sy + 1.4, 'pedestal');
  st.addFloor(pedestalFloor(s.x, sy + 1.4, s.z), 'pedestal');
  ctx.spawn('marsh.shrine', new Transform(), combine(...shrine));

  // stepping stones / mud banks near the near island (cosmetic)
  const rocks: Instance[] = [];
  for (let i = 0; i < 30; i++) {
    const isl = rng.pick(MARSH_ISLANDS);
    const a = rng.range(0, 6.3), r = isl.r + rng.range(0, 3);
    const x = isl.x + Math.cos(a) * r, z = isl.z + Math.sin(a) * r;
    rocks.push({ pos: new Vec3(x, h(x, z) - 0.2, z), yaw: rng.range(0, 6.3), scale: rng.range(0.5, 1.2) });
  }
  instanced(ctx, 'marsh.rocks', translate(cylinder(0.5, 0.7, 0.5, 6, [0.28, 0.3, 0.26]), 0, 0.25, 0), rocks);

  // reed glow update: rewrite emissive mix for reeds within 8 m of the player
  const buf = reedInst.buf;
  let frame = 0;
  return {
    update(playerPos: Vec3): void {
      if ((frame++ & 3) !== 0) return;
      let changed = false;
      for (let i = 0; i < reeds.length; i++) {
        const p = reeds[i].pos;
        const d = p.distanceToXZ(playerPos);
        const target = d < 9 ? (1 - d / 9) * 1.4 : 0;
        const o = i * INSTANCE_STRIDE + 19;
        const cur = buf[o];
        const next = cur + (target - cur) * 0.25;
        if (Math.abs(next - cur) > 0.002) { buf[o] = next; changed = true; }
      }
      if (changed) reedInst.gm.setInstances(buf, true);
    },
  };
}

function pedestalFloor(x: number, y: number, z: number): AABB {
  return AABB.fromMinMax(x - 0.6, y - 1, z - 0.6, x + 0.6, y, z + 0.6);
}
void GpuMesh;
